// Roku Ads Manager conversion tracking.
//
import { AD_CONSENT_EVENT, adTrackingAllowed } from "@/lib/ad-consent";
//
// Verified against Roku's official docs:
// - Base pixel + PAGE_VIEW: https://help.ads.roku.com/en/articles/7062023-setting-up-javascript-pixels-and-events
// - Event catalog (Sign Up, Purchase, Start Trial, Subscribe, ...):
//   https://help.ads.roku.com/en/articles/13907239-event-tracking-and-reporting-faq
// - `value` (order total) + `currency` enable ROAS reporting on PURCHASE.
//
// The rkp stub queues events before the loader script arrives, so every
// call is safe even when the CDN script is blocked or slow.

declare global {
  interface Window {
    rkp?: ((...args: unknown[]) => void) & {
      queue?: unknown[];
      load?: (src: string) => void;
      initiatorVersion?: string;
    };
  }
}

export const ROKU_PIXEL_ID = "PaccQ5EbMEUn";
const ROKU_LOADER_URL = "https://cdn.ravm.tv/ust/dist/rkp.loader.js";

let initialized = false;
let lastPageViewPath: string | null = null;
let consentListenerInstalled = false;

function rkp(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  try {
    window.rkp?.(...args);
  } catch {
    // Tracking must never break the app.
  }
}

/**
 * Installs the Roku base pixel exactly once and fires the initial PAGE_VIEW.
 * Safe to call repeatedly; subsequent calls are no-ops. Never loads without
 * explicit advertising consent (see ad-consent.ts) — the pixel is a
 * third-party ad tracker, so no consent means no script, no network call.
 */
export function initRokuPixel(): void {
  if (typeof window === "undefined" || initialized) return;
  if (!adTrackingAllowed()) return;
  initialized = true;
  try {
    if (!window.rkp) {
      // Official Roku snippet (verbatim semantics, typed for TS).
      const w = window;
      const d = document;
      const stub = function (this: unknown, ...fnArgs: unknown[]) {
        const e = Array.prototype.slice.call(fnArgs) as unknown[];
        e.push(Date.now());
        const self = stub as NonNullable<Window["rkp"]> & { eventProcessor?: (...a: unknown[]) => void };
        if (self.eventProcessor) self.eventProcessor.apply(self, e);
        else self.queue!.push(e);
      } as NonNullable<Window["rkp"]>;
      stub.initiatorVersion = "1.0";
      stub.queue = [];
      stub.load = (src: string) => {
        const t = d.createElement("script");
        t.async = true;
        t.src = src;
        const n = d.getElementsByTagName("script")[0];
        (n ? n.parentNode : d.body)?.insertBefore(t, n ?? null);
      };
      w.rkp = stub;
      stub.load(ROKU_LOADER_URL);
    }
    rkp("init", ROKU_PIXEL_ID);
    rkp("event", "PAGE_VIEW");
    lastPageViewPath = window.location.pathname;
  } catch {
    // Fail gracefully if the script is blocked.
  }
}

/**
 * Call once at app start. Initializes immediately when consent was already
 * given; otherwise waits for the consent event and initializes then. When the
 * visitor declines (or sends Global Privacy Control) the pixel never loads.
 */
export function initRokuPixelWhenConsented(): void {
  if (typeof window === "undefined") return;
  if (adTrackingAllowed()) {
    initRokuPixel();
    return;
  }
  if (consentListenerInstalled) return;
  consentListenerInstalled = true;
  window.addEventListener(AD_CONSENT_EVENT, () => {
    if (adTrackingAllowed()) initRokuPixel();
  });
}

/**
 * SPA page-view tracking. The base pixel only fires on full loads, so call
 * this on every client-side navigation. Consecutive duplicate paths (initial
 * load, re-renders) are suppressed.
 */
export function trackRokuPageView(path: string): void {
  if (!initialized || path === lastPageViewPath) return;
  lastPageViewPath = path;
  rkp("event", "PAGE_VIEW");
}

/** Fires once per browser session per key, surviving confirmation-page refreshes. */
function fireOncePerSession(storageKey: string, fire: () => void): void {
  try {
    if (window.sessionStorage.getItem(storageKey)) return;
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // Storage unavailable (private mode) — still fire; Roku dedupes server-side.
  }
  fire();
}

/**
 * Confirmed account creation. Call only after auth confirms the account
 * exists — never on button clicks or logins.
 */
export function trackRokuSignup(method: "email" | "google" | "apple" = "email"): void {
  fireOncePerSession("roku_signup_fired", () => {
    rkp("event", "SIGN_UP", { method });
  });
}

/**
 * Confirmed paid subscription. Call only after the subscription is verified
 * active server-side. Free trials must NOT call this (trial start is not a
 * paid purchase). `value` is the order total; `currency` an ISO code.
 */
export function trackRokuPurchase(value: number, currency = "USD"): void {
  fireOncePerSession("roku_purchase_fired", () => {
    rkp("event", "PURCHASE", { value, currency });
  });
}

/**
 * New subscription confirmed server-side (active OR trialing). This is the
 * subscription-lifecycle event; PURCHASE separately marks the paid ones.
 */
export function trackRokuSubscribe(value: number, currency = "USD", status: "active" | "trialing"): void {
  fireOncePerSession("roku_subscribe_fired", () => {
    rkp("event", "SUBSCRIBE", { value, currency, status });
  });
}

/** Free trial successfully activated (confirmed server-side, not a click). */
export function trackRokuStartTrial(): void {
  fireOncePerSession("roku_trial_fired", () => {
    rkp("event", "START_TRIAL");
  });
}

/**
 * A real checkout session was created server-side and the customer is being
 * sent to it. Never fire this on button clicks alone.
 */
export function trackRokuInitiateCheckout(value: number, currency = "USD"): void {
  fireOncePerSession("roku_checkout_fired", () => {
    rkp("event", "INITIATE_CHECKOUT", { value, currency });
  });
}

/**
 * Registration flow completed (role chosen on the onboarding screen). For
 * accounts created and completed in one step this overlaps SIGN_UP — Roku
 * reports should treat them as one funnel, not additive counts.
 */
export function trackRokuCompleteRegistration(): void {
  fireOncePerSession("roku_registration_fired", () => {
    rkp("event", "COMPLETE_REGISTRATION");
  });
}
